import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function parseDate(value) {
  if (!value) return null;
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatAmount(value) {
  const amount = Number(value || 0);
  return `${amount >= 0 ? '+' : '-'}${formatCurrency(Math.abs(amount))}`;
}

function matchesPeriod(date, mode, month, year) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }

  const now = new Date();
  const todayKey = now.toISOString().slice(0, 10);
  const dateKey = date.toISOString().slice(0, 10);

  switch (mode) {
    case 'today':
      return dateKey === todayKey;
    case 'month':
      return date.getFullYear() === Number(year) && date.getMonth() === Number(month);
    case 'year':
      return date.getFullYear() === Number(year);
    case 'all':
    default:
      return true;
  }
}

function buildCashStats(entries) {
  return entries.reduce(
    (acc, entry) => {
      const amount = Number(entry.amount || 0);
      acc.balance += amount;
      if (amount >= 0) {
        acc.in += amount;
      } else {
        acc.out += Math.abs(amount);
      }
      return acc;
    },
    { balance: 0, in: 0, out: 0 }
  );
}

export function CashbookView({ crewId, embedded = false, onChanged = null }) {
  const navigate = useNavigate();
  const nonce = window?.pfandpiratData?.nonce ?? '';
  const api = '/wp-json/pfandpirat/v1/cashbook';

  const now = new Date();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [type, setType] = useState('deposit');

  const [periodMode, setPeriodMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const monthOptions = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  const yearOptions = useMemo(() => {
    const years = new Set([now.getFullYear()]);
    entries.forEach(entry => {
      const date = parseDate(entry.created_at || entry.date || entry.timestamp);
      if (date) {
        years.add(date.getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [entries, now]);

  const uniqueUsers = useMemo(
    () => Array.from(new Set(entries.map(entry => entry.display_name || entry.user_name || entry.user || 'Unbekannt'))),
    [entries]
  );

  useEffect(() => {
    setPage(1);
  }, [periodMode, selectedMonth, selectedYear, filterType, filterUser]);

  const fetchEntries = async () => {
    if (!crewId) {
      setEntries([]);
      onChanged?.([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${api}?crew_id=${crewId}`, {
        credentials: 'include',
        headers: { 'X-WP-Nonce': nonce },
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const normalized = Array.isArray(data) ? data : [];
      setEntries(normalized);
      onChanged?.(normalized);
    } catch (err) {
      console.error('[Cashbook] load failed:', err);
      setError('Einträge konnten nicht geladen werden.');
      setEntries([]);
      onChanged?.([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [crewId]); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizedEntries = useMemo(
    () => entries.map(entry => ({
      ...entry,
      _parsedDate: parseDate(entry.created_at || entry.date || entry.timestamp),
      _userLabel: entry.display_name || entry.user_name || entry.user || 'Unbekannt',
    })),
    [entries]
  );

  const periodEntries = useMemo(
    () => normalizedEntries.filter(entry => matchesPeriod(entry._parsedDate, periodMode, selectedMonth, selectedYear)),
    [normalizedEntries, periodMode, selectedMonth, selectedYear]
  );

  const visibleEntries = useMemo(
    () =>
      periodEntries.filter(entry => {
        if (filterType !== 'all' && entry.type !== filterType) {
          return false;
        }
        if (filterUser !== 'all' && entry._userLabel !== filterUser) {
          return false;
        }
        return true;
      }),
    [periodEntries, filterType, filterUser]
  );

  const overallStats = useMemo(() => buildCashStats(normalizedEntries), [normalizedEntries]);
  const periodStats = useMemo(() => buildCashStats(periodEntries), [periodEntries]);

  const totalPages = Math.max(1, Math.ceil(visibleEntries.length / itemsPerPage));
  const paginatedEntries = visibleEntries.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleAdd = async event => {
    event.preventDefault();
    const numericAmount = Number(String(amount).replace(',', '.'));

    if (!crewId) {
      setError('Bitte zuerst eine Crew auswählen.');
      return;
    }

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError('Bitte einen gültigen Betrag größer 0 eingeben.');
      return;
    }

    setError(null);

    try {
      const body = {
        crew_id: crewId,
        amount: numericAmount,
        reason,
        type,
      };

      const response = await fetch(api, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const newEntry = await response.json();
      setEntries(prev => {
        const updated = [newEntry, ...prev];
        onChanged?.(updated);
        return updated;
      });
      setAmount('');
      setReason('');
      setType('deposit');
    } catch (err) {
      console.error('[Cashbook] add failed:', err);
      setError('Speichern fehlgeschlagen.');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Eintrag wirklich löschen?')) {
      return;
    }

    try {
      const response = await fetch(`${api}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-WP-Nonce': nonce },
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      setEntries(prev => {
        const updated = prev.filter(entry => Number(entry.id) !== Number(id));
        onChanged?.(updated);
        return updated;
      });
    } catch (err) {
      console.error('[Cashbook] delete failed:', err);
      alert('Löschen nicht möglich – fehlende Berechtigung?');
    }
  };

  const currentPeriodLabel =
    periodMode === 'month'
      ? `${monthOptions[selectedMonth]} ${selectedYear}`
      : periodMode === 'year'
        ? `Jahr ${selectedYear}`
        : periodMode === 'today'
          ? 'Heute'
          : 'Gesamt';

  return (
    <div className={`pfp-cashbook ${embedded ? 'pfp-cashbook--embedded' : ''}`}>
      {!embedded && (
        <button className="pfp-cashbook__back" onClick={() => navigate('/crews')}>
          ← zurück
        </button>
      )}

      <div className="pfp-cashbook__header">
        <div>
          <h2 className="pfp-cashbook__title">🏴‍☠️ Crew-Kassenbuch</h2>
          <p className="pfp-cashbook__subtitle">Einzahlungen, Entnahmen und Kassenstand deiner Crew.</p>
        </div>
        <div className="pfp-cashbook__period-label">Aktive Ansicht: {currentPeriodLabel}</div>
      </div>

      <div className="pfp-cashbook__stats-grid">
        <div className="pfp-cashbook__stat-card">
          <div className="pfp-cashbook__stat-label">Kassen-Saldo</div>
          <div className="pfp-cashbook__stat-value">{formatCurrency(overallStats.balance)}</div>
          <div className="pfp-cashbook__stat-note">Aktueller Gesamtstand der Crew-Kasse</div>
        </div>
        <div className="pfp-cashbook__stat-card pfp-cashbook__stat-card--positive">
          <div className="pfp-cashbook__stat-label">Einzahlungen im Zeitraum</div>
          <div className="pfp-cashbook__stat-value">{formatCurrency(periodStats.in)}</div>
          <div className="pfp-cashbook__stat-note">Positive Buchungen für {currentPeriodLabel}</div>
        </div>
        <div className="pfp-cashbook__stat-card pfp-cashbook__stat-card--negative">
          <div className="pfp-cashbook__stat-label">Entnahmen im Zeitraum</div>
          <div className="pfp-cashbook__stat-value">{formatCurrency(periodStats.out)}</div>
          <div className="pfp-cashbook__stat-note">Negative Buchungen für {currentPeriodLabel}</div>
        </div>
      </div>

      <div className="pfp-cashbook__toolbar">
        <div className="pfp-cashbook__segmented">
          <button
            type="button"
            className={periodMode === 'today' ? 'is-active' : ''}
            onClick={() => setPeriodMode('today')}
          >
            Heute
          </button>
          <button
            type="button"
            className={periodMode === 'month' ? 'is-active' : ''}
            onClick={() => setPeriodMode('month')}
          >
            Monat
          </button>
          <button
            type="button"
            className={periodMode === 'year' ? 'is-active' : ''}
            onClick={() => setPeriodMode('year')}
          >
            Jahr
          </button>
          <button
            type="button"
            className={periodMode === 'all' ? 'is-active' : ''}
            onClick={() => setPeriodMode('all')}
          >
            Gesamt
          </button>
        </div>

        <div className="pfp-cashbook__toolbar-selects">
          <select
            value={selectedMonth}
            onChange={event => {
              setSelectedMonth(Number(event.target.value));
              setPeriodMode('month');
            }}
          >
            {monthOptions.map((label, index) => (
              <option key={label} value={index}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={event => {
              setSelectedYear(Number(event.target.value));
              setPeriodMode('year');
            }}
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select value={filterType} onChange={event => setFilterType(event.target.value)}>
            <option value="all">Alle Typen</option>
            <option value="deposit">Einzahlungen</option>
            <option value="withdrawal">Entnahmen</option>
          </select>
          <select value={filterUser} onChange={event => setFilterUser(event.target.value)}>
            <option value="all">Alle Benutzer</option>
            {uniqueUsers.map(user => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="pfp-cashbook__error">{error}</div>}

      <div className="pfp-cashbook__content-grid">
        <form className="pfp-cashbook__form-card" onSubmit={handleAdd}>
          <div className="pfp-cashbook__card-title">Neuer Kassenbuch-Eintrag</div>
          <div className="pfp-cashbook__form-grid">
            <div>
              <label>Typ</label>
              <select value={type} onChange={event => setType(event.target.value)}>
                <option value="deposit">Einzahlung</option>
                <option value="withdrawal">Entnahme</option>
              </select>
            </div>
            <div>
              <label>Betrag (€)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={event => setAmount(event.target.value)}
                required
              />
            </div>
            <div className="pfp-cashbook__form-span-full">
              <label>Grund / Notiz</label>
              <input
                type="text"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder="z. B. Pfand eingelöst oder Entnahme für Crew-Ausgabe"
              />
            </div>
          </div>
          <button type="submit" className="pfp-cashbook__submit">
            Eintrag hinzufügen
          </button>
        </form>

        <div className="pfp-cashbook__table-card">
          <div className="pfp-cashbook__table-head">
            <div className="pfp-cashbook__card-title">Kassenbuch-Historie</div>
            <div className="pfp-cashbook__table-meta">{visibleEntries.length} Einträge</div>
          </div>

          {loading ? (
            <div className="pfp-cashbook__empty">Lade …</div>
          ) : paginatedEntries.length === 0 ? (
            <div className="pfp-cashbook__empty">Keine Buchungen für diese Filter gefunden.</div>
          ) : (
            <>
              <div className="pfp-cashbook__table-wrap">
                <table className="pfp-cashbook__table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Benutzer</th>
                      <th>Grund</th>
                      <th>Typ</th>
                      <th className="is-right">Betrag</th>
                      <th className="is-right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map(entry => {
                      const date = entry._parsedDate;
                      const isPositive = Number(entry.amount || 0) >= 0;

                      return (
                        <tr key={entry.id}>
                          <td>
                            {date ? date.toLocaleDateString('de-DE') : '—'}
                            <span>{date ? date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </td>
                          <td>{entry._userLabel}</td>
                          <td>{entry.reason || '—'}</td>
                          <td>{entry.type === 'withdrawal' ? 'Entnahme' : 'Einzahlung'}</td>
                          <td className={`is-right ${isPositive ? 'is-positive' : 'is-negative'}`}>
                            {formatAmount(entry.amount)}
                          </td>
                          <td className="is-right">
                            <button type="button" className="pfp-cashbook__delete" onClick={() => handleDelete(entry.id)}>
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4}>Saldo</td>
                      <td className="is-right">{formatCurrency(overallStats.balance)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="pfp-cashbook__pagination">
                <button type="button" onClick={() => setPage(current => Math.max(1, current - 1))} disabled={page === 1}>
                  Zurück
                </button>
                <span>
                  Seite {page} / {totalPages}
                </span>
                <button type="button" onClick={() => setPage(current => Math.min(totalPages, current + 1))} disabled={page === totalPages}>
                  Weiter
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CashbookView;
